import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {BasicBitmapCountUp} from "../winPresentations/countUp/BasicBitmapCountUp";
import {Mental} from "../Mental";
import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {IMentalGameData} from "../server/MentalData";
import {MentalGameMode} from "../server/MentalGameMode";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {TimelineLite, TweenLite, TweenMax} from "gsap";
import {SoundManager} from "../sound/SoundManager";
import {CustomWiggle} from "gsap/dist/CustomWiggle";

export class MentalWinCapView extends PIXI.Container implements StageLayer {
    private _gameData: IMentalGameData;

    private _winCapBg: PIXI.Graphics;
    private _winCapSpine: PIXI.spine.Spine;
    private _winCapOldMovieEffect: PIXI.spine.Spine;
    private _countUp: BasicBitmapCountUp;
    private _winCapTitle: PIXI.Sprite;
    private _winCapX: PIXI.Sprite;

    private _endTl: TimelineLite;
    private _wiggleTweenWinCap: TweenLite;
    private _wiggleTweenOldMovie: TweenLite;

    private _button: ContinueButton;
    private _buttonCallBack: () => void;

    constructor() {
        super();

        CustomWiggle.create("wiggle", {wiggles: 22, type: "uniform"});

        this.createWinCapItems();

        this.initContinueButton();
    }

    private static resetWinCapSpineItem(item: PIXI.spine.Spine): void {
        item.updateTransform();
        item.visible = true;
        item.alpha = 0;
        item.skeleton.setToSetupPose();
        item.skeleton.setSlotsToSetupPose();
        item.state.setEmptyAnimation(0, 0);
        item.state.clearTracks();
    }

    public onResize(): void {
        if (NolimitApplication.isLandscape) {
            this._button.position.set(360, 615);
        } else {
            const centerBottom: number = (NolimitApplication.screenBounds.bottom - 610) * 0.5 + 610;
            this._button.position.set(360, centerBottom);
        }
    }

    public playWinCapAnimations(callBackFn: () => void): void {
        const tl: TimelineLite = new TimelineLite();

        tl.add(() => this.beginAnimation(callBackFn), 0);

        tl.add(() => {
            this._countUp.y = 300;
            this._countUp.scale.set(1);
            this._countUp.preCalculateScale(0, this._gameData.accumulatedRoundWin);

            this._countUp.setText("");

            this._countUp.getCountAnimation(0, this._gameData.accumulatedRoundWin, 7.1);
            this._countUp.visible = true;
        }, 0.2);

        tl.add(() => {
            TweenLite.to(this._countUp.scale, 0.133, {x: 1.5, y: 1.5});
            TweenLite.to(this._countUp.scale, 0.2, {x: 1, y: 1, delay: 0.133});
        }, 7.3);

        tl.add(() => {
            this._wiggleTweenWinCap.pause();
            this._wiggleTweenOldMovie.pause();
            this._wiggleTweenWinCap.kill();
            this._wiggleTweenOldMovie.kill();

            // #321
            if (Mental.isSoftWinCap) {
                this._winCapSpine.position.set(360, 800);
            } else {
                this._winCapSpine.position.set(360, 520);
            }
            this._winCapOldMovieEffect.position.set(360);
        }, 7.5);

        tl.add(() => {
            TweenLite.to(this._countUp, {y: 420, duration: 0.1});
            TweenLite.to(this._countUp.scale, {x: 0, y: 0, duration: 0.1});
        }, 7.9); // time difference from previous time is more to keep the final amount on screen for a bit.

        tl.add(() => {
            this._winCapTitle.visible = true;
            this._winCapTitle.alpha = 0;
            this._winCapTitle.scale.set(1.2);

            this._winCapX.visible = true;
            this._winCapX.alpha = 0;
            this._winCapX.scale.set(1.2);

            TweenLite.to([this._winCapTitle, this._winCapX], 0.1, {alpha: 1});
        }, 8);

        tl.add(() => {
            this._button.visible = true;
            this._button.alpha = 1;
            this._button.enable(true);

            this._countUp.visible = false;
            this._winCapSpine.state.clearTracks();
            this._winCapOldMovieEffect.state.clearTracks();
        }, 8.2);

        tl.add(() => this.animateEndTl(), 8.4);
    }

    private beginAnimation(callBackFn: () => void): void {
        Mental.keypadPlugin.hide();
        this._buttonCallBack = callBackFn;
        this._gameData = Mental.model.gameData;

        this._winCapBg.visible = true;
        this._winCapBg.alpha = 0;

        MentalWinCapView.resetWinCapSpineItem(this._winCapSpine);
        this._winCapSpine.state.setAnimation(0, "animation", false);

        MentalWinCapView.resetWinCapSpineItem(this._winCapOldMovieEffect);
        this._winCapOldMovieEffect.state.setAnimation(0, "old_film_effect_animation", true);

        // #321
        this.prepareWinCapSpine();

        this._wiggleTweenOldMovie = TweenMax.to(this._winCapOldMovieEffect, {
            x: 361,
            y: 361,
            duration: 7.5,
            ease: "wiggle"
        });

        TweenLite.to([this._winCapBg, this._winCapSpine, this._winCapOldMovieEffect], {
            alpha: 1,
            duration: 0.2
        });

        // #256
        SoundManager.startWinCap();
    }

    private createWinCapItems(): void {
        this._winCapBg = new PIXI.Graphics();
        this._winCapBg.visible = false;
        this._winCapBg.beginFill(0x000000);
        this._winCapBg.drawRect(-360, -360, 1680, 1680);
        this._winCapBg.endFill();

        this._winCapSpine = new PIXI.spine.Spine(Mental.assetLoader.getSpineAsset("winCapSpine"));
        this._winCapSpine.visible = false;
        const mask: PIXI.Graphics = new PIXI.Graphics();
        mask.beginFill(0x000000);
        mask.drawRect(-280, 0, 1280, 720);
        mask.endFill();
        this._winCapSpine.mask = mask;

        this._winCapOldMovieEffect = new PIXI.spine.Spine(Mental.assetLoader.getSpineAsset("winCapOldFilmEffect"));
        this._winCapOldMovieEffect.visible = false;
        this._winCapOldMovieEffect.scale.set(1, 1.4);
        this._winCapOldMovieEffect.position.set(360);

        this._countUp = new BasicBitmapCountUp({
            fontName: "MentalNumbersWinCountUp",
            fontSize: 30,
            tint: 0xffffff
        }, 720, new PIXI.Point(0.5));
        this._countUp.visible = false;
        this._countUp.position.set(360, 300);

        this._winCapTitle = new PIXI.Sprite(Mental.assetLoader.getTextures("winCapTitle")[0]);
        this._winCapTitle.visible = false;
        this._winCapTitle.position.set(360, 260);
        this._winCapTitle.anchor.set(0.5);

        this._winCapX = new PIXI.Sprite(Mental.assetLoader.getTextures("winCapX")[0]);
        this._winCapX.visible = false;
        this._winCapX.position.set(360, 420);
        this._winCapX.anchor.set(0.5);

        this.addChild(this._winCapBg, this._winCapSpine, mask, this._winCapOldMovieEffect, this._countUp, this._winCapTitle, this._winCapX);
    }

    private initContinueButton(): void {
        this._button = new ContinueButton();
        this._button.visible = false;
        this._button.enable(false);
        this._button.pivot.set(this._button.width / 2, this._button.height / 2);
        this._button.addClickCallback(() => this.onButtonClick());

        this.onResize();

        this.addChild(this._button);
    }

    // #321 => due to replay, need to set it everytime as JUR gets set late in replay, but this file gets constructed early.
    private prepareWinCapSpine(): void {
        if (Mental.isSoftWinCap) {
            this._winCapSpine.scale.set(1.5);
            this._winCapSpine.position.set(360, 800);

            this._wiggleTweenWinCap = TweenMax.to(this._winCapSpine, {
                x: 361,
                y: 801,
                duration: 7.5,
                ease: "wiggle"
            });
        } else {
            this._winCapSpine.position.set(360, 520);

            this._wiggleTweenWinCap = TweenMax.to(this._winCapSpine, {
                x: 361,
                y: 521,
                duration: 7.5,
                ease: "wiggle"
            });
        }
    }

    // do not create this tl at init, if you do make sure the end result is still same
    private animateEndTl(): void {
        // #276
        const duration: number = 3;

        this._endTl = new TimelineLite({
            paused: true,
            onComplete: () => this.animateEndTl()
        });

        // decrease scale
        this._endTl.to([this._winCapTitle.scale, this._winCapX.scale], {
            duration: duration,
            x: 1,
            y: 1
        }, 0);

        // fade out
        this._endTl.to([this._winCapTitle, this._winCapX], {
            duration: duration,
            alpha: 0.1
        }, 0);

        // fade in
        this._endTl.to([this._winCapTitle, this._winCapX], {
            duration: duration,
            alpha: 1
        }, duration);

        // increase scale
        this._endTl.to([this._winCapTitle.scale, this._winCapX.scale], {
            duration: duration,
            x: 1.2,
            y: 1.2
        }, duration);

        this._endTl.play(0);
    }

    private onButtonClick(): void {
        Mental.bigWinPresentation.hideOverlay();
        Mental.keypadPlugin.show();
        this._button.enable(false);

        this.removeWinCapItems();

        // if win cap as FS outro
        if (this._gameData.mode.indexOf("FREESPIN") > -1 && this._gameData.nextMode === MentalGameMode.NORMAL) {
            Mental.deadFeatureStickyMultiplier.hideSymbolStickers();
            Mental.stickyScatter.removeStickyScatteronPos();

            // #107
            Mental.fireFrame.exitFS();

            // #131
            Mental.background.flickBackgroundLight(true);
        }

        this._buttonCallBack();

        // #256
        SoundManager.stopWinCap();
    }

    private removeWinCapItems(): void {
        this._endTl.pause();
        this._endTl.kill();

        TweenLite.to([this._winCapBg, this._winCapSpine, this._winCapOldMovieEffect, this._winCapTitle, this._winCapX, this._button], {
            alpha: 0,
            duration: 0.3,
            onComplete: () => {
                this._winCapBg.visible = false;
                this._winCapSpine.visible = false;
                this._winCapOldMovieEffect.visible = false;
                this._winCapTitle.visible = false;
                this._winCapX.visible = false;
                this._button.visible = false;
            }
        });
    }
}